import React, { useState, useEffect } from "react";
import { Table, Checkbox, Button } from "antd";

interface Module {
  key: string;
  name: string;
  permissions: string[];
}

interface Permission {
  key: string;
  name: string;
}

interface PermissionMatrixProps {
  modules: Module[];
  permissions: Permission[];
  value?: string[];
  onChange?: (selectedPermissions: string[]) => void;
}

const PermissionMatrixComponent: React.FC<PermissionMatrixProps> = ({
  modules,
  permissions,
  value = [],
  onChange,
}) => {
  const [selected, setSelected] = useState<Record<string, string[]>>({});

  useEffect(() => {
    const initialSelected: Record<string, string[]> = {};
    value.forEach((perm) => {
      const [moduleKey, permissionKey] = perm.split(".");
      if (moduleKey && permissionKey) {
        if (!initialSelected[moduleKey]) {
          initialSelected[moduleKey] = [];
        }
        initialSelected[moduleKey].push(permissionKey);
      }
    });
    setSelected(initialSelected);
  }, [value]);

  const triggerChange = (newSelected: Record<string, string[]>) => {
    const flatPermissions: string[] = [];
    for (const moduleKey in newSelected) {
      newSelected[moduleKey].forEach((permissionKey) => {
        flatPermissions.push(`${moduleKey}.${permissionKey}`);
      });
    }
    if (onChange) {
      onChange(flatPermissions);
    }
  };

  const handlePermissionChange = (
    moduleKey: string,
    permissionKey: string,
    isChecked: boolean
  ) => {
    const newSelected = { ...selected };
    if (isChecked) {
      if (!newSelected[moduleKey]) {
        newSelected[moduleKey] = [];
      }
      newSelected[moduleKey].push(permissionKey);
    } else {
      newSelected[moduleKey] = (newSelected[moduleKey] || []).filter(
        (p) => p !== permissionKey
      );
      if (newSelected[moduleKey].length === 0) {
        delete newSelected[moduleKey];
      }
    }
    setSelected(newSelected);
    triggerChange(newSelected);
  };

 const handleModuleChange = (module: Module, isChecked: boolean) => {
  const newSelected = { ...selected };

  if (isChecked) {
    newSelected[module.key] = [...module.permissions];
  } else {
    delete newSelected[module.key];
  }

  setSelected(newSelected);
  triggerChange(newSelected);
};

  const handleGrantAll = (isChecked: boolean) => {
  const newSelected: Record<string, string[]> = {};

  if (isChecked) {
    modules.forEach((module) => {
      newSelected[module.key] = [...module.permissions];
    });
  }

  setSelected(newSelected);
  triggerChange(newSelected);
};
  const allPermissionKeys = permissions.map((p) => p.key);
  const isAllGranted = modules.every(
  (module) =>
    selected[module.key]?.length === module.permissions.length
  );

const columns = [
  {
    title: (
      <Checkbox
        checked={isAllGranted}
        onChange={(e) => handleGrantAll(e.target.checked)}
      >
        <span style={{ color: "#fff" }}>Module</span>
      </Checkbox>
    ),
    dataIndex: "name",
    key: "module",
    fixed: "left" as const,
    render: (name: string, record: Module) => (
      <Checkbox
        checked={
          selected[record.key]?.length === record.permissions.length
        }
        onChange={(e) =>
          handleModuleChange(record, e.target.checked)
        }
      >
        {name}
      </Checkbox>
    ),
  },

  ...permissions.map((permission) => ({
    title: permission.name,
    key: permission.key,
    align: "center" as const,
    render: (_: any, record: Module) => {
      const hasPermission = record.permissions.includes(permission.key);

      return hasPermission ? (
        <Checkbox
          checked={selected[record.key]?.includes(permission.key)}
          onChange={(e) =>
            handlePermissionChange(
              record.key,
              permission.key,
              e.target.checked
            )
          }
        />
      ) : ( <span style={{ color: '#999', fontWeight: 500 }}>-</span> );
    },
  })),
];

  return (
    <>
      <Button
        onClick={() => handleGrantAll(true)}
        style={{ marginBottom: 16 }}
        disabled={isAllGranted}
      >
        Grant All Permissions
      </Button>
      <Table
        dataSource={modules}
        columns={columns}
        pagination={false}
        bordered
        size="small"
        rowKey="key"
         scroll={{ 
          x: "max-content",
          y: 500,
         }}
         sticky
      />
    </>
  );
};

export default PermissionMatrixComponent;